// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import HashtagText from '@/components/social-wall/HashtagText';
import ReactionDisplay from '@/components/social-wall/ReactionDisplay';
import LinkPreviewCard from '@/components/social-wall/LinkPreviewCard';
import StatsCard from '@/components/social-wall/StatsCard';

interface SharedPost {
  id: string;
  profile_id: string;
  content: string | null;
  media_urls: string[] | null;
  post_type: string;
  link_url: string | null;
  link_preview: any;
  stats_data: any;
  reactions_count: Record<string, number>;
  comments_count: number;
  share_count: number;
  created_at: string;
  profiles: {
    id: string;
    name: string | null;
    avatar_url: string | null;
    username: string | null;
    sport: string | null;
  } | null;
}

const SharedPostPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const [post, setPost] = useState<SharedPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) return;

      try {
        const { data, error } = await (supabase as any)
          .from('wall_posts')
          .select(`
            id,
            profile_id,
            content,
            media_urls,
            post_type,
            link_url,
            link_preview,
            stats_data,
            reactions_count,
            comments_count,
            share_count,
            created_at,
            profiles:profile_id (
              id,
              name,
              avatar_url,
              username,
              sport
            )
          `)
          .eq('id', postId)
          .eq('is_hidden', false)
          .single();

        if (error) throw error;
        // Handle single object profiles result
        const postData = data as any;
        if (Array.isArray(postData.profiles)) {
          postData.profiles = postData.profiles[0] || null;
        }
        setPost(postData as SharedPost);
      } catch (err: any) {
        setError(err.message || 'Post not found');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId]);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getOgDescription = () => {
    if (!post) return 'Check out this post on RecruitLook';
    if (post.content) {
      return post.content.length > 160 
        ? post.content.substring(0, 157) + '...' 
        : post.content;
    }
    return 'Check out this post on RecruitLook';
  };

  const getOgImage = () => {
    if (post?.media_urls && post.media_urls.length > 0) {
      return post.media_urls[0];
    }
    if (post?.profiles?.avatar_url) {
      return post.profiles.avatar_url;
    }
    return `${window.location.origin}/og-default.png`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <h1 className="text-2xl font-bold mb-2">Post Not Found</h1>
        <p className="text-muted-foreground mb-4">This post may have been deleted or is no longer available.</p>
        <Button asChild>
          <Link to="/">Go to RecruitLook</Link>
        </Button>
      </div>
    );
  }

  const totalReactions = Object.values(post.reactions_count || {}).reduce((a, b) => a + b, 0);

  return (
    <>
      <Helmet>
        <title>{post.profiles?.name || 'Athlete'} on RecruitLook</title>
        <meta name="description" content={getOgDescription()} />
        <meta property="og:title" content={`${post.profiles?.name || 'Athlete'} on RecruitLook`} />
        <meta property="og:description" content={getOgDescription()} />
        <meta property="og:image" content={getOgImage()} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.profiles?.name || 'Athlete'} on RecruitLook`} />
        <meta name="twitter:description" content={getOgDescription()} />
        <meta name="twitter:image" content={getOgImage()} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-[#002E62] to-[#001a3a]">
        {/* Header */}
        <header className="border-b border-white/10 bg-[#002E62]/80 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-8 w-8 bg-[#BC8D09] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">RL</span>
              </div>
              <span className="text-white font-bold text-xl">RecruitLook</span>
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="container mx-auto px-4 py-8 max-w-2xl">
          <Card className="overflow-hidden">
            <CardContent className="pt-6">
              {/* Author */}
              <div className="flex items-center gap-3 mb-4">
                <Link to={`/profile/${post.profiles?.username || post.profile_id}`}>
                  <Avatar className="h-12 w-12">
                    <AvatarImage
                      src={post.profiles?.avatar_url || undefined}
                      alt={post.profiles?.name || 'User'}
                    />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {getInitials(post.profiles?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div>
                  <Link
                    to={`/profile/${post.profiles?.username || post.profile_id}`}
                    className="font-semibold hover:underline"
                  >
                    {post.profiles?.name || 'Unknown User'}
                  </Link>
                  {post.profiles?.sport && (
                    <p className="text-sm text-muted-foreground">{post.profiles.sport}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>

              {/* Content */}
              {post.content && (
                <p className="mb-4 whitespace-pre-wrap text-lg">
                  <HashtagText content={post.content} />
                </p>
              )}

              {/* Media */}
              {post.media_urls && post.media_urls.length > 0 && (
                <div className="mb-4 rounded-lg overflow-hidden">
                  {post.media_urls.map((url, index) => (
                    url.match(/\.(mp4|mov|webm|ogg)$/i) ? (
                      <video
                        key={index}
                        src={url}
                        controls
                        className="w-full max-h-[500px] bg-muted"
                      />
                    ) : (
                      <img
                        key={index}
                        src={url}
                        alt={`Post media ${index + 1}`}
                        className="w-full object-cover"
                      />
                    )
                  ))}
                </div>
              )}

              {/* Link Preview */}
              {post.link_url && post.link_preview && (
                <div className="mb-4">
                  <LinkPreviewCard
                    preview={{
                      title: post.link_preview.title || null,
                      description: post.link_preview.description || null,
                      image: post.link_preview.image || null,
                      domain: post.link_preview.domain || new URL(post.link_url).hostname.replace('www.', ''),
                      isYouTube: post.link_preview.isYouTube,
                      videoId: post.link_preview.videoId,
                    }}
                    url={post.link_url}
                  />
                </div>
              )}

              {/* Stats Card */}
              {post.post_type === 'stats' && post.stats_data && (
                <div className="mb-4">
                  <StatsCard statsData={post.stats_data} />
                </div>
              )}

              {/* Reactions Display */}
              <ReactionDisplay
                reactionsCount={post.reactions_count || {}}
                userReactions={[]}
                onReactionClick={() => {}}
              />

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground pt-3 border-t mt-3">
                <span>{totalReactions} reactions</span>
                <span>{post.comments_count || 0} comments</span>
                {post.share_count > 0 && <span>{post.share_count} shares</span>}
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="mt-6 text-center">
            <p className="text-white/80 mb-4">See more on RecruitLook</p>
            <Button asChild size="lg" className="bg-[#BC8D09] hover:bg-[#a57a08] text-white">
              <Link to="/">
                <ExternalLink className="h-4 w-4 mr-2" />
                View More on RecruitLook
              </Link>
            </Button>
          </div>
        </main>
      </div>
    </>
  );
};

export default SharedPostPage;